// Keep Projects exports independent from the table's 12-row pagination.
// The same /orders/get-orders endpoint enforces instance/rep scope and excludes
// archived projects; these filters reflect the currently applied URL query.
export const PROJECT_EXPORT_PAGE_SIZE = 100;

export const buildProjectReportExportFilters = ({
  search = "",
  instanceUniqueId,
  userId,
} = {}) => {
  const params = new URLSearchParams(search);
  const tab = params.get("tab") || "myOrders";
  const filters = {
    instanceUniqueId,
    tab,
    orderSearchQuery: params.get("orderSearchQuery") ?? params.get("q") ?? "",
  };
  const customerId = params.get("customerId");
  if (customerId) filters.customerId = customerId;
  if (tab === "myOrders" && userId != null) filters.assignedTo = userId;
  if (tab === "allOrders") {
    const soStatus = params.get("soStatus");
    const poStatus = params.get("poStatus");
    if (soStatus) filters.soStatus = soStatus;
    if (poStatus) filters.poStatus = poStatus;
  }
  return filters;
};

// Paginate rather than requesting an unbounded result set from the database.
// Fail closed if any page is missing, repeated, or inconsistent: never save a
// PDF/CSV/Excel file that claims to contain more projects than it actually does.
export const fetchAllProjectExportRows = async (
  requestPage,
  filters,
  { pageSize = PROJECT_EXPORT_PAGE_SIZE } = {},
) => {
  let page = 1;
  let expectedTotal = null;
  const rows = [];
  const seen = new Set();

  do {
    const response = await requestPage({ ...filters, page, rpp: pageSize });
    const batch = response?.Results;
    const total = Number(response?.TotalResults);
    if (!Array.isArray(batch) || !Number.isSafeInteger(total) || total < 0) {
      throw new Error("Invalid Projects export response.");
    }
    if (expectedTotal === null) expectedTotal = total;
    if (total !== expectedTotal) {
      throw new Error("Project count changed during export.");
    }

    for (const row of batch) {
      const key = row?.id != null
        ? `id:${row.id}`
        : row?.OrderId != null ? `order:${row.OrderId}` : null;
      if (!key || seen.has(key)) {
        throw new Error("Project rows changed during export.");
      }
      seen.add(key);
      rows.push(row);
    }
    if (rows.length > expectedTotal || (rows.length < expectedTotal && batch.length === 0)) {
      throw new Error("Projects export is incomplete.");
    }
    page += 1;
  } while (rows.length < expectedTotal);

  return rows;
};

export const sortProjectExportRows = (rows, sortBy, sortDir, canonicalizeSoDisplay = (value) => value) => {
  if (!sortBy) return rows;
  return [...rows].sort((a, b) => {
    let aVal = "";
    let bVal = "";
    if (sortBy === "so_status") {
      aVal = canonicalizeSoDisplay(a.OrderStatus?.SO || "");
      bVal = canonicalizeSoDisplay(b.OrderStatus?.SO || "");
    } else if (sortBy === "project_number") {
      aVal = String(a.OrderId ?? "");
      bVal = String(b.OrderId ?? "");
    } else if (sortBy === "project_name") {
      aVal = String(a.ProjectName ?? "");
      bVal = String(b.ProjectName ?? "");
    } else if (sortBy === "date_created") {
      aVal = String(a.createdAt ?? "");
      bVal = String(b.createdAt ?? "");
    } else {
      aVal = String(a[sortBy] ?? "");
      bVal = String(b[sortBy] ?? "");
    }
    return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });
};
